import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cardTypeDescriptionMapping } from '../..';
import useMount from '../../../../../../../common/hooks/useMount';
import Button from '../../../../../../../common/widgets/Button';
import { ErrorPanel } from '../../../../../../../common/widgets/ErrorPanel';
import Icons from '../../../../../../../common/widgets/Icons';
import Loader from '../../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../../common/widgets/LoadingSpinner';
import {
  CardFormFactor,
  CardType,
  FormFactorSelectorContext,
} from '../../../../../enums';
import { resources } from '../../../../../resources/resources';
import { cardService } from '../../../../../services/card';

interface FormFactorSelectorProps {
  cardType: CardType;
  selectedCardFormFactor?: CardFormFactor;
  onCardFormFactorChange: (formFactor: CardFormFactor) => void;
  onButtonClick: () => void;
  contextType: FormFactorSelectorContext;
  isButtonLoading?: boolean;
}

export const FormFactorSelector = (props: FormFactorSelectorProps) => {
  const {
    cardType,
    selectedCardFormFactor,
    onCardFormFactorChange,
    onButtonClick,
    contextType,
    isButtonLoading,
  } = props;
  const [formFactorTypes, setFormFactorTypes] = useState<
    CardFormFactor[] | undefined
  >();
  const [showErrorPanel, setShowErrorPanel] = useState<boolean>(false);
  const { t: translate } = useTranslation();

  const loadFormFactorOptionType = async (
    abortController?: AbortController,
  ) => {
    const resp = await cardService.getFormFactorOptionType(cardType, {
      signal: abortController?.signal,
    });
    if (abortController?.signal.aborted) {
      return;
    }

    if (resp.hasErrors) {
      setShowErrorPanel(true);
    } else {
      if (resp.data.length === 0) {
        setShowErrorPanel(true);
      } else {
        if (resp.data.length === 1) {
          onCardFormFactorChange(resp.data[0]);
        } else {
          onCardFormFactorChange(CardFormFactor.Virtual);
        }
        setFormFactorTypes(resp.data);
      }
    }
  };

  const getTitle = () => {
    const cardName = translate(
      _.get(resources, cardTypeDescriptionMapping[cardType]),
    );

    if (contextType === FormFactorSelectorContext.Buy) {
      return translate(resources.employeeCards.buy.step2.title, {
        0: cardName,
      });
    }

    return translate(resources.employeeCards.reissue.title, {
      0: cardName,
    });
  };

  useMount(loadFormFactorOptionType);

  const showPlasticCard = formFactorTypes?.includes(CardFormFactor.Plastic);

  const showVirtualCard = formFactorTypes?.includes(CardFormFactor.Virtual);

  if (showErrorPanel) {
    return <ErrorPanel />;
  }

  return (
    <>
      <h1 className="card-step-title">{getTitle()}</h1>
      <Loader loader={<LoadingSpinner />} loading={!formFactorTypes}>
        <ul className="card-form-factor-selector">
          {showVirtualCard && (
            <li
              className={`${
                selectedCardFormFactor === CardFormFactor.Virtual
                  ? 'card-checked'
                  : ''
              }`}
              onClick={() => onCardFormFactorChange(CardFormFactor.Virtual)}
            >
              <div className="css-check"></div>
              <div className="css-image">
                <Icons.BadgeConfetti />
                <div>
                  <Icons.CardVirtual />
                </div>
              </div>
              <div className="css-details">
                <div className="css-name">
                  {translate(resources.employeeCards.buy.step2.virtual.title)}
                </div>
                <div className="css-status">
                  <span className="av-nm">
                    {translate(
                      resources.employeeCards.topUp.availableNextMonth,
                    )}
                  </span>
                </div>
                <ul className="css-info">
                  <li>
                    {translate(resources.employeeCards.buy.step2.virtual.info1)}
                  </li>
                  <li>
                    {translate(resources.employeeCards.buy.step2.virtual.info2)}
                  </li>
                </ul>
              </div>
            </li>
          )}
          {showPlasticCard && (
            <li
              className={`${
                selectedCardFormFactor === CardFormFactor.Plastic
                  ? 'card-checked'
                  : ''
              }`}
              onClick={() => onCardFormFactorChange(CardFormFactor.Plastic)}
            >
              <div className="css-check"></div>
              <div className="css-image">
                <Icons.BadgeConfetti />
                <div>
                  <Icons.CardPlastic />
                </div>
              </div>
              <div className="css-details">
                <div className="css-name">
                  {translate(resources.employeeCards.buy.step2.plastic.title)}
                </div>
                <div className="css-status">
                  <span className="av-nm">
                    {translate(
                      resources.employeeCards.topUp.availableNextMonth,
                    )}
                  </span>
                </div>
                <ul className="css-info">
                  <li>
                    {translate(resources.employeeCards.buy.step2.plastic.info1)}
                  </li>
                  <li>
                    {translate(resources.employeeCards.buy.step2.plastic.info2)}
                  </li>
                </ul>
              </div>
            </li>
          )}
        </ul>
        <div className="css-action">
          <Button
            onClick={onButtonClick}
            disabled={!selectedCardFormFactor}
            loading={!!isButtonLoading}
            className="btn btn-solid btn-lg"
          >
            <span className="btn-text">
              {contextType === FormFactorSelectorContext.Buy
                ? translate(resources.continue)
                : translate(resources.employeeCards.reissue.button)}
            </span>
          </Button>
        </div>
      </Loader>
    </>
  );
};
